import { useCallback, useLayoutEffect, useState } from 'react';
import debounce from 'lodash.debounce';

const initial = {
  overflowing: false,
  scroll: { start: false, end: false },
};

/**
 * Check if connected target element is overflowing horizontally
 *
 * @param {React.RefObject<HTMLElement | null>} ref - Target ref
 * @return {[boolean, { start: boolean, end: boolean }]} Whether the target is
 *   overflowing, and whether scroll is at the very beginning (start) or end
 */
export default ref => {
  const [{ overflowing, scroll }, setState] = useState(initial);

  const check = useCallback(() => {
    // ?? not a default: an unmounted useRef(null) target is null, not undefined
    const { offsetWidth, scrollLeft, scrollWidth } = ref.current ?? {};
    setState({
      overflowing: offsetWidth < scrollWidth,
      scroll: {
        start: scrollLeft <= 0,
        end: scrollLeft >= scrollWidth - offsetWidth,
      },
    });
  }, [ref]);

  useLayoutEffect(() => {
    const { current: $target } = ref;
    const handler = debounce(check, 500);

    check();

    window.addEventListener('resize', handler);
    if ($target) {
      $target.addEventListener('scroll', handler);
    }

    return () => {
      window.removeEventListener('resize', handler);
      if ($target) {
        $target.removeEventListener('scroll', handler);
      }
    };
  }, [check, ref]);

  return [overflowing, scroll];
};
